// Shared-const values: the compile-time VALUE of a `shared const`, plus the
// comparison + formatting the deployment mount uses to check that co-mounted
// members agree (docs/language.md §"Shared constants").
//
// The compiler (the Rust/WASM module) evaluates the constant subset of the
// expression grammar and emits each shared const's value in its result blob;
// the WASM adapter decodes them into `ConstValue`s. This module is only the
// value type + the host-side agreement check over it — it does not evaluate
// source (that lived in the retired TS compiler).
// ----- comparison + formatting (used by the mount-time agreement check) -----
export function constValuesEqual(a, b) {
    if (a.kind !== b.kind)
        return false;
    switch (a.kind) {
        case "int": return a.value === b.value;
        case "bool": return a.value === b.value;
        case "string": {
            const bb = b.bytes;
            if (a.bytes.length !== bb.length)
                return false;
            for (let i = 0; i < a.bytes.length; i++)
                if (a.bytes[i] !== bb[i])
                    return false;
            return true;
        }
        case "array": {
            const be = b.elements;
            if (a.elements.length !== be.length)
                return false;
            for (let i = 0; i < a.elements.length; i++) {
                if (!constValuesEqual(a.elements[i], be[i]))
                    return false;
            }
            return true;
        }
    }
}
export function formatConstValue(v) {
    switch (v.kind) {
        case "int": return v.value.toString();
        case "bool": return v.value ? "true" : "false";
        case "string": {
            const text = new TextDecoder().decode(v.bytes);
            return JSON.stringify(text);
        }
        case "array": {
            const parts = v.elements.map(formatConstValue);
            const shown = parts.length > 12 ? parts.slice(0, 12).concat("…") : parts;
            return `[${shown.join(", ")}]`;
        }
    }
}
// A short human explanation of WHY two shared-const values disagree, for the
// deployment-start diagnostic: the first structural point they diverge.
export function describeConstDifference(a, b) {
    if (a.kind !== b.kind)
        return `type ${a.kind} vs ${b.kind}`;
    if (a.kind === "array" && b.kind === "array") {
        if (a.elements.length !== b.elements.length) {
            return `array length ${a.elements.length} vs ${b.elements.length}`;
        }
        for (let i = 0; i < a.elements.length; i++) {
            if (!constValuesEqual(a.elements[i], b.elements[i])) {
                return `element ${i} differs (${formatConstValue(a.elements[i])} vs ${formatConstValue(b.elements[i])})`;
            }
        }
    }
    return `${formatConstValue(a)} vs ${formatConstValue(b)}`;
}
//# sourceMappingURL=const-eval.js.map